import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { AddressMismatchModal } from '../components/modals/AddressMismatchModal';
import { NowActiveOrderBanner } from '../components/now/NowActiveOrderBanner';
import { NowFeaturedCategories } from '../components/now/NowFeaturedCategories';
import { NowHeroCarousel } from '../components/now/NowHeroCarousel';
import { NowLimitedTimeDeal } from '../components/now/NowLimitedTimeDeal';
import { NowFloatingProductAd } from '../components/now/NowFloatingProductAd';
import { NowFloatingDeliveryStatus } from '../components/now/NowFloatingDeliveryStatus';
import { NowConnectionNotice } from '../components/now/NowConnectionNotice';
import { NowProductRail } from '../components/now/NowProductRail';
import { NowQuickCategories } from '../components/now/NowQuickCategories';
import { NowGoalSelector } from '../components/now/NowGoalSelector';
import { NowRxServiceEntry } from '../components/now/NowRxServiceEntry';
import { NowRecentSearches } from '../components/now/NowRecentSearches';
import { NowSavedQuickLists } from '../components/now/NowSavedQuickLists';
import { NowStickyCart } from '../components/now/NowStickyCart';
import { NowTopDeals } from '../components/now/NowTopDeals';
import { LazySection } from '../components/shared/LazySection';
import { healthProducts, babyProducts } from '../data/products';
import { useStore } from '../lib/store';
import { useOnlineStatus } from '../lib/hooks/useOnlineStatus';
type LocationValue = {
  name: string;
  address?: string;
  type?: string;
} | null;
const detectedDeliveryAddress = '2214 Prince Mohammed Bin Salman Rd, Al Yasmin, Riyadh, Saudi Arabia';
const NowCampaignSliders = lazy(() => import('../components/now/NowCampaignSliders').then(module => ({
  default: module.NowCampaignSliders
})));
const DoubleBanner = lazy(() => import('../components/home/DoubleBanner').then(module => ({
  default: module.DoubleBanner
})));
const PromoBanners = lazy(() => import('../components/home/PromoBanners').then(module => ({
  default: module.PromoBanners
})));
const NowNeedsCarousel = lazy(() => import('../components/now/NowVisualCarousels').then(module => ({
  default: module.NowNeedsCarousel
})));
const NowShopByStore = lazy(() => import('../components/now/NowShopByStore').then(module => ({
  default: module.NowShopByStore
})));
const NowBrandsCarousel = lazy(() => import('../components/now/NowVisualCarousels').then(module => ({
  default: module.NowBrandsCarousel
})));
const NowShopByVideo = lazy(() => import('../components/now/NowShopByVideo').then(module => ({
  default: module.NowShopByVideo
})));
const MultiBanner = lazy(() => import('../components/home/MultiBanner').then(module => ({
  default: module.MultiBanner
})));
function DeferredNowSection({
  children
}: {
  children: React.ReactNode;
}) {
  return <LazySection fallback={<div className="min-h-48" data-id="element-7340" data-mp="2amx2n5aaq5tw" />} rootMargin="320px" data-id="element-7339" data-mp="26nehq9ih5ur2"><Suspense fallback={<div className="min-h-48" data-id="element-7342" data-mp="2an6rgpjlu6y0" />} data-id="element-7341" data-mp="2anonbl2cuj5e">{children}</Suspense></LazySection>;
}
export function NowHomePage() {
  const navigate = useNavigate();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showAddressMismatch, setShowAddressMismatch] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  const cart = useStore(state => state.cart);
  const cartCount = useStore(state => state.cartCount());
  const total = useStore(state => state.cartTotal());
  const activeNowOrder = useStore(state => state.activeNowOrder);
  const isOnline = useOnlineStatus();
  const createNowOrder = useStore(state => state.createNowOrder);
  const updateNowOrderStatus = useStore(state => state.updateNowOrderStatus);
  useEffect(() => {
    if (activeNowOrder) return;
    createNowOrder('NOW-120-DEMO');
    updateNowOrderStatus('out-for-delivery');
  }, [activeNowOrder, createNowOrder, updateNowOrderStatus]);
  const nowProducts = useMemo(() => healthProducts.map((product, index) => ({
    ...product,
    expressDelivery: true,
    availabilityStatus: index === 0 ? 'unavailable' as const : 'available' as const
  })), []);
  const buyAgainProducts = useMemo(() => healthProducts.slice(0, 5).map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const runningLowProducts = useMemo(() => [...babyProducts.slice(0, 3), ...healthProducts.slice(5, 7)].map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const popularNearYou = useMemo(() => [...healthProducts.slice(2, 6), ...babyProducts.slice(0, 2)].map(product => ({
    ...product,
    expressDelivery: true,
    isBestSeller: true
  })), []);
  const featuredProducts = useMemo(() => healthProducts.slice(0, 5).map(product => ({
    ...product,
    expressDelivery: true
  })), []);
  const topDealProducts = useMemo(() => [...healthProducts.slice(0, 2).map(product => ({
    ...product,
    dealCategory: 'Vitamins' as const
  })), ...healthProducts.slice(2, 5).map(product => ({
    ...product,
    dealCategory: 'Health' as const
  })), ...babyProducts.slice(0, 3).map(product => ({
    ...product,
    dealCategory: 'Baby' as const
  }))], []);
  const hasActiveOrder = Boolean(activeNowOrder);
  const handleLocationSelect = (location: NonNullable<LocationValue>) => {
    setSelectedLocation(location);
    setLocationModalOpen(false);
    const selectedAddress = location.address ?? location.name;
    if (location.type === 'delivery' && selectedAddress !== detectedDeliveryAddress) {
      window.setTimeout(() => setShowAddressMismatch(true), 180);
    }
  };
  return <HelmetProvider data-id="element-7343" data-mp="1o90rfyhwczst">
      <div className="min-h-screen bg-[#f7f8fb]" data-id="element-7344" data-mp="1oabs3pg1prfp">
        <SEO title="NOW 120 | Pharmacy essentials delivered fast" data-id="element-7345" data-mp="3kcoxakvg7mc4" />
        <NowConnectionNotice isOnline={isOnline} data-id="element-7346" data-mp="3kavp3156su20" />

        <div className="hidden md:block" data-id="element-7347" data-mp="3fen4v9c4uanz">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={handleLocationSelect} showAnnouncement data-id="element-7348" data-mp="3ffooi277s5hb" />
        </div>

        <main className="pb-24 pt-0 md:pb-0 md:pt-[var(--header-height-desktop)]" data-id="element-7349" data-mp="6wh6sifygque">
          <div className="md:hidden" data-id="element-7350" data-mp="2vf7y2pyagsy">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} transparentAtTop showExperienceSwitcher activeExperience="innova" data-id="element-7351" data-mp="2yjgtded20py" />
          </div>

          <NowHeroCarousel data-id="element-7352" data-mp="2inuw6wrj3kw8" />
          <NowFeaturedCategories data-id="element-7353" data-mp="2doi15oqi77ik" />
          <NowQuickCategories data-id="element-7354" data-mp="2dpjnwqgvtig4" />
          <div className="mx-auto max-w-7xl px-4 pt-5 md:px-6" data-id="element-7355" data-mp="2dqcmh45ot1n6"><NowRxServiceEntry data-id="element-7356" data-mp="t5xs08jyrab6" /></div>
          <NowGoalSelector data-id="element-7357" data-mp="t6zcf3mvd95k" />

          <div className="mx-auto max-w-7xl space-y-9 px-4 py-7 md:space-y-12 md:px-6 md:py-10" data-id="element-7358" data-mp="t8tp5unzluyk">
            <NowRecentSearches data-id="element-7359" data-mp="y7entpnlc6kw" />
            {hasActiveOrder && <NowActiveOrderBanner onTrack={() => navigate('/track-order?mode=now')} data-id="element-7360" data-mp="jfmogn46z34t" />}
            <NowSavedQuickLists data-id="element-7361" data-mp="oez68tg7hk0j" />
            <NowTopDeals products={topDealProducts} data-id="element-7362" data-mp="odxvkq33qzjc" />
            <NowLimitedTimeDeal data-id="element-7363" data-mp="afgaz18hu35n" />
            <DeferredNowSection data-id="element-7364" data-mp="aeentye7dq7k"><NowCampaignSliders data-id="element-7365" data-mp="ai2ix1zaqkoe" /></DeferredNowSection>
            <DeferredNowSection data-id="element-7366" data-mp="ffcekzda3sko"><DoubleBanner data-id="element-7367" data-mp="ffu8vqgdrwpy" /></DeferredNowSection>

            <NowProductRail title="Deal Zone" subtitle="Fast-delivery offers available near you" viewAllLink="/offers" products={nowProducts} data-id="element-7368" data-mp="htkrilqup6d" />

            <NowProductRail title="Buy again" subtitle="Your everyday essentials, ready to add" viewAllLink="/account/orders" products={buyAgainProducts} data-id="element-7369" data-mp="1uvb9h8xvxs2z" />

            <NowProductRail title="Running low?" subtitle="Restock your pharmacy and family essentials" viewAllLink="/category/personal-care?from=now" products={runningLowProducts} data-id="element-7370" data-mp="nkj0dptv4f8c" />

            <NowProductRail title="Recommended for you" subtitle="Fast-moving pharmacy picks in your area" viewAllLink="/category/medicines?from=now" products={popularNearYou} data-id="element-7371" data-mp="8wo6i644c4to" />

            <DeferredNowSection data-id="element-7372" data-mp="3w0b64fmqv0y2"><PromoBanners data-id="element-7373" data-mp="3vzt0wti3tce0" /></DeferredNowSection>
            <DeferredNowSection data-id="element-7374" data-mp="3r21a62t73wbe"><NowNeedsCarousel data-id="element-7375" data-mp="3r1hr2irt71vs" /></DeferredNowSection>
            <DeferredNowSection data-id="element-7376" data-mp="3r446f4kxylok"><NowShopByStore data-id="element-7377" data-mp="3m3g4voz36mg3" /></DeferredNowSection>

            <NowProductRail title="Vitamins & Supplements" subtitle="Support your everyday wellness" viewAllLink="/category/vitamins-supplements?from=now" products={featuredProducts} data-id="element-7378" data-mp="3m4razvg086ad" />

            <DeferredNowSection data-id="element-7379" data-mp="1rvvv6kyeblse"><NowBrandsCarousel data-id="element-7380" data-mp="1rub08u8660u0" /></DeferredNowSection>
            <DeferredNowSection data-id="element-7381" data-mp="1rti04c3pu9ku"><NowShopByVideo products={featuredProducts.slice(0, 3)} data-id="element-7382" data-mp="1wu5t33bi90hp" /></DeferredNowSection>
            <DeferredNowSection data-id="element-7383" data-mp="21l5lbv9oiudv"><MultiBanner data-id="element-7384" data-mp="21km6woj8mo4l" /></DeferredNowSection>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-7385" data-mp="26k8rjj1eb6nf"><Footer data-id="element-7386" data-mp="26kicgs7l2nm9" /></div>
        <MobileFooterNav data-id="element-7387" data-mp="26jpcca34qwd3" />
        {activeNowOrder && <NowFloatingDeliveryStatus orderId={activeNowOrder.id} status={activeNowOrder.status} estimatedDelivery={activeNowOrder.estimatedDelivery} isDelayed={activeNowOrder.isDelayed} revisedEstimatedDelivery={activeNowOrder.revisedEstimatedDelivery} onTrack={() => navigate('/track-order?mode=now')} data-id="element-7388" data-mp="18wq9tiprau7a" />}
        <NowFloatingProductAd product={featuredProducts[0]} data-id="element-7389" data-mp="1ntheo7i78y4a" />
        {cart.length > 0 && <NowStickyCart itemCount={cartCount} total={total} onViewCart={() => navigate('/cart?mode=now')} data-id="element-7390" data-mp="2ua3562tjgz8q" />}
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={handleLocationSelect} data-id="element-7391" data-mp="2z731vn4i0pn6" />
        <AddressMismatchModal isOpen={showAddressMismatch} selectedAddress={selectedLocation?.address ?? selectedLocation?.name ?? ''} detectedLocation={detectedDeliveryAddress} onClose={() => setShowAddressMismatch(false)} onConfirm={() => setShowAddressMismatch(false)} onChangeAddress={() => {
        setShowAddressMismatch(false);
        setLocationModalOpen(true);
      }} data-id="element-7392" data-mp="3en6cytl4f03h" />
      </div>
    </HelmetProvider>;
}